"use client"

import type { Dispatch, SetStateAction } from "react"
import { createContext } from "react"

import clsxd from "../../helpers/clsx-dedupe"

import type { TJobFilters } from "./jobs/JobFilters"

export interface LayoutContext {
  className?: string
  darkMode: boolean
  searchIsOpen: boolean
}

export interface BioModalContext {
  modalOpen: boolean
  name?: string
  title?: string
  bio?: string
  company?: string
  linkedin?: string
  lightmode?: boolean
}

export interface JobFiltersModalContext {
  modalOpen: boolean
  filters?: TJobFilters
}

export interface GlobalPencilBanner {
  isVisible: boolean
}

export interface ImpactReportPartnersModalContext {
  modalOpen: boolean
  contentData?: any
}

export interface ImpactReportSpotlightModalContext {
  modalOpen: boolean
  contentData?: {
    __typename?: string
    title?: string
    heroAsset?: {
      __typename?: string
      title?: string
      url?: string
      width?: number
      height?: number
    } | null
    video?: {
      __typename?: string
      videoName?: string
      videoLink?: string
      wistiaVideo?: string | null
    } | null
    description?: {
      __typename?: string
      json?: any
    }
    statsCollection?: {
      __typename?: string
      items?: Array<any>
    }
    logosCollection?: {
      __typename?: string
      items?: Array<any>
    }
    button?: {
      __typename?: string
      type?: string
      ctaLink?: string
      ctaText?: string
      variant?: "primary" | "secondary" | "tertiary"
      theme?: "dark" | "light" | "blue"
      size?: "lg" | "md" | "sm" | "xs"
    }
  }
}

export interface AppContextInterface {
  currentLayout: LayoutContext
  bioModal: BioModalContext
  jobFiltersModal: JobFiltersModalContext
  globalPencilBanner: GlobalPencilBanner
  impactReportPartnersModal: ImpactReportPartnersModalContext
  impactReportSpotlightModal: ImpactReportSpotlightModalContext
  showHeaderFooter: boolean
}

const defaultSetAppContext = () => {
  throw new Error("setAppContext function must be overridden")
}

export const initialAppContext: AppContextInterface = {
  currentLayout: {
    className: "dark",
    // TODO: https://trello.com/c/pN27X9GS/313-add-ability-to-swap-between-dark-and-light-modes
    darkMode: true,
    searchIsOpen: false,
  },
  bioModal: {
    modalOpen: false,
    name: undefined,
    title: undefined,
    bio: undefined,
    company: undefined,
    linkedin: undefined,
    lightmode: false,
  },
  jobFiltersModal: {
    modalOpen: false,
    filters: undefined,
  },
  globalPencilBanner: {
    isVisible: true,
  },
  impactReportPartnersModal: {
    modalOpen: false,
    contentData: null,
  },
  impactReportSpotlightModal: {
    modalOpen: false,
    contentData: null,
  },
  showHeaderFooter: true,
}

export class Context {
  private context: AppContextInterface

  public setAppContext: Dispatch<SetStateAction<AppContextInterface>>

  constructor(
    context?: AppContextInterface,
    setAppContext?: Dispatch<SetStateAction<AppContextInterface>>,
  ) {
    this.context = context ?? initialAppContext
    this.setAppContext = setAppContext ?? defaultSetAppContext
  }

  get currentLayout(): LayoutContext {
    return this.context.currentLayout
  }

  public setCurrentLayout(layout: Partial<LayoutContext>): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          ...layout,
        },
      }
      return context
    })
  }

  public addClassName(className: string): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          className: clsxd(prevContext.currentLayout.className, className),
        },
      }
      return context
    })
  }

  public removeClassName(className: string): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          className: clsxd(
            prevContext.currentLayout.className?.replace(className, ""),
          ),
        },
      }
      return context
    })
  }

  get searchIsOpen(): boolean {
    return this.context.currentLayout.searchIsOpen
  }

  public openSearch(): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          searchIsOpen: true,
        },
      }
      return context
    })
  }

  public closeSearch(): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          searchIsOpen: false,
        },
      }
      return context
    })
  }

  public toggleSearchOpen(): void {
    if (this.context.currentLayout.searchIsOpen === true) {
      this.closeSearch()
    } else {
      this.openSearch()
    }
  }

  public setDarkMode(): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          darkMode: true,
          className: clsxd(this.context.currentLayout.className, "dark"),
        },
      }
      return context
    })
  }

  public setLightMode(): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        currentLayout: {
          ...prevContext.currentLayout,
          darkMode: false,
          className: clsxd(
            this.context.currentLayout.className?.replace("dark", ""),
          ),
        },
      }
      return context
    })
  }

  public toggleDarkMode(): void {
    if (this.context.currentLayout.darkMode === true) {
      this.setLightMode()
    } else {
      this.setDarkMode()
    }
  }

  get bioModal(): BioModalContext {
    return this.context.bioModal
  }

  public setBioModal(
    modalOpen: boolean,
    name?: string,
    title?: string,
    bio?: string,
    company?: string,
    linkedin?: string,
    lightmode?: boolean,
  ): void {
    const context = {
      ...this.context,
      bioModal: {
        modalOpen,
        name,
        title,
        bio,
        company,
        linkedin,
        lightmode,
      },
    }
    this.setAppContext(context)
  }

  get jobFiltersModal(): JobFiltersModalContext {
    return this.context.jobFiltersModal
  }

  public setJobFiltersModal(modalOpen: boolean, filters?: TJobFilters): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        jobFiltersModal: {
          modalOpen,
          filters,
        },
      }
      return context
    })
  }

  get globalPencilBanner(): GlobalPencilBanner {
    return this.context.globalPencilBanner
  }

  public setGlobalPencilBanner(isVisible: boolean): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        globalPencilBanner: {
          isVisible,
        },
      }
      return context
    })
  }

  public setImpactReportPartnersModal(
    modalOpen: boolean,
    contentData?: any,
  ): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        impactReportPartnersModal: {
          modalOpen,
          contentData,
        },
      }
      return context
    })
  }

  get impactReportPartnersModal(): ImpactReportPartnersModalContext {
    return this.context.impactReportPartnersModal
  }

  public setImpactReportSpotlightModal(
    modalOpen: boolean,
    contentData?: any,
  ): void {
    this.setAppContext((prevContext) => {
      const context = {
        ...prevContext,
        impactReportSpotlightModal: {
          modalOpen,
          contentData,
        },
      }
      return context
    })
  }

  get impactReportSpotlightModal(): ImpactReportSpotlightModalContext {
    return this.context.impactReportSpotlightModal
  }

  get showHeaderFooter(): boolean {
    return this.context.showHeaderFooter
  }

  public setShowHeaderFooter(show: boolean): void {
    this.setAppContext((prevContext) => ({
      ...prevContext,
      showHeaderFooter: show,
    }))
  }
}

// The Context class constructor gets called and is used to create a React Context object.
// Since the constructor is not passed any args it uses the initialAppContext object declared earlier in the file.
const AppContext = createContext<Context>(new Context())
export default AppContext
